import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-closed_at' };

const createState = () => ({
    closures: [],
    closures_url: '/api/connectors/pos/closures/',
    closures_filters: { ...DEFAULT_FILTERS },
    closures_count: 0,
    closures_total_count: null,
    closures_total_loading: false,
    closures_loaded: false,
    closures_loading: false,
    closures_loading_errors: null,
    closures_cancel_source: null,
    closures_stats: {},
    closures_stats_loading: false,
    closures_cancel_source_stats: null,
    fetch_stats: true,
});

const mutations = {
    updateClosures(state, closures) {
        state.closures = closures;
    },
    updateClosuresFilters(state, closures_filters) {
        state.closures_filters = closures_filters;
    },
    updateClosuresLoading(state, closures_loading) {
        state.closures_loading = closures_loading;
    },
    updateClosuresLoaded(state, closures_loaded) {
        state.closures_loaded = closures_loaded;
    },
    updateClosuresLoadingErrors(state, closures_loading_errors) {
        state.closures_loading_errors = closures_loading_errors;
    },
    updateClosuresCount(state, closures_count) {
        state.closures_count = closures_count;
    },
    updateClosuresTotalCount(state, closures_total_count) {
        state.closures_total_count = closures_total_count;
    },
    updateClosuresTotalLoading(state, closures_total_loading) {
        state.closures_total_loading = closures_total_loading;
    },
    updateClosuresCancelSource(state, source) {
        state.closures_cancel_source = source;
    },
    updateClosuresStats(state, closures_stats) {
        state.closures_stats = closures_stats || {};
    },
    updateClosuresStatsLoading(state, closures_stats_loading) {
        state.closures_stats_loading = closures_stats_loading;
    },
    updateClosuresCancelSourceStats(state, source) {
        state.closures_cancel_source_stats = source;
    },
    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
};

const actions = {
    async fetchClosuresTotal({ commit, state }) {
        if (store.getters['session/current_user_permissions'].indexOf('connectors.view_pos_sync') === -1) {
            return;
        }

        commit('updateClosuresTotalLoading', true);
        commit('updateClosuresTotalCount', null);
        try {
            const establishmentId = state.closures_filters.establishment;
            if (!establishmentId) {
                commit('updateClosuresTotalCount', 0);
                commit('updateClosures', []);
                commit('updateClosuresCount', 0);
                commit('updateClosuresStats', {});
                return 0;
            }
            const response = await axios.get(state.closures_url, {
                params: {
                    establishment: establishmentId,
                    limit: 0,
                },
            });
            const count = response.data.count ?? 0;
            commit('updateClosuresTotalCount', count);
            if (count === 0) {
                commit('updateClosures', []);
                commit('updateClosuresCount', 0);
                commit('updateClosuresLoaded', true);
                commit('updateClosuresStats', {});
            }
            return count;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateClosuresTotalLoading', false);
        }
    },

    async fetchClosuresStats({ commit, state }) {
        if (store.getters['session/current_user_permissions'].indexOf('connectors.view_pos_sync') === -1) {
            return;
        }
        if (!state.fetch_stats) {
            return;
        }
        if (!state.closures_filters.establishment) {
            commit('updateClosuresStats', {});
            return;
        }

        if (state.closures_cancel_source_stats) {
            state.closures_cancel_source_stats.cancel('New stats fetch started');
        }
        const cancelSource = axios.CancelToken.source();
        commit('updateClosuresCancelSourceStats', cancelSource);
        commit('updateClosuresStatsLoading', true);

        try {
            const response = await axios.get(`${state.closures_url}stats/`, {
                params: state.closures_filters,
                cancelToken: cancelSource.token,
            });
            commit('updateClosuresStats', response.data);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateClosuresStatsLoading', false);
        }
    },

    async fetchClosures({ commit, state, dispatch }) {
        if (store.getters['session/current_user_permissions'].indexOf('connectors.view_pos_sync') === -1) {
            return;
        }

        commit('updateClosuresLoading', true);
        commit('updateClosuresLoadingErrors', null);

        if (state.closures_cancel_source) {
            state.closures_cancel_source.cancel('canceled');
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateClosuresCancelSource', cancelSource);
        try {
            const response = await axios.get(state.closures_url, {
                params: state.closures_filters,
                cancelToken: cancelSource.token,
            });
            commit('updateClosures', response.data.results);
            commit('updateClosuresCount', response.data.count);
            commit('updateClosuresCancelSource', null);
            commit('updateClosuresLoaded', true);
            dispatch('fetchClosuresStats');
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateClosuresLoadingErrors', error.details);
            commit('updateClosuresCancelSource', null);
            throw error;
        } finally {
            commit('updateClosuresLoading', false);
        }
    },

    async init({ commit }, params) {
        await commit('updateClosures', []);
        await commit('updateClosuresTotalCount', null);
        await commit('updateClosuresStats', {});
        await commit('updateClosuresLoaded', false);
        await commit('updateFetchStats', params.fetch_stats === undefined ? true : params.fetch_stats);
        await commit('updateClosuresFilters', {
            ...DEFAULT_FILTERS,
            ...(params.filters || {}),
        });
    },

    /** Re-fetch totals, list and stats using current filters (tab revisit, same route). */
    async reload({ dispatch, state }) {
        if (!state.closures_filters.establishment) {
            return;
        }
        await dispatch('fetchClosuresTotal');
        if ((state.closures_total_count || 0) > 0) {
            await dispatch('fetchClosures');
        }
    },
};

export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    actions,
});
